/**
 * Árvore da Visualização em Sunburst: Empresa → Instalação → Ativo.
 *
 * A hierarquia é fixa e não acompanha o Agrupamento do Painel — no Sunburst o
 * agrupamento é a própria forma do gráfico.
 *
 * A folha é sempre o Ativo. Sensor não vira anel: 600 Sensores em 360° dariam
 * 0,6° por setor, sem rótulo possível e com alvo menor que o dedo. Sensor
 * aparece no tooltip do Ativo.
 *
 * Cada Sensor pesa 1 e os anéis de dentro somam os filhos, então a geometria só
 * muda quando a frota muda — nunca por status, nunca entre ciclos. É a mesma
 * razão pela qual a ordem da Grade é estável: memória espacial de quem vigia.
 *
 * O peso é o Sensor, e não o Ativo, porque contar Ativos quase não separava as
 * Instalações: são 152 Ativos em 81 Instalações, média de 1,9, e quase todas
 * ficavam entre 2,4° e 4,7°. Contando Sensores a mesma frota abre para 524
 * unidades, e uma Instalação bem instrumentada chega a ocupar sete vezes o
 * espaço de uma pequena.
 */
import { assetStates } from '@/domain/enums/assetState';

export const sunburstLevels = {
  COMPANY: 'company',
  INSTALLATION: 'installation',
  ASSET: 'asset',
  SENSOR: 'sensor',
};

/**
 * Nível do anel mais interno.
 *
 * Instalação é o padrão: é o recorte que quem vigia procura primeiro, e gastar o
 * anel de dentro com Empresa espreme os outros dois. Empresa continua disponível
 * para quem opera mais de uma.
 */
export const DEFAULT_SUNBURST_ROOT = sunburstLevels.INSTALLATION;

/**
 * Traduz o Agrupamento do Painel para a raiz do gráfico.
 *
 * Ativo não é raiz possível: sobraria um anel só, que é uma rosca.
 *
 * @param {String} grouping Um valor de `groupingLevels`.
 * @returns {String} `company` ou `installation`.
 */
export function normalizeSunburstRoot(grouping) {
  return grouping === sunburstLevels.COMPANY
    ? sunburstLevels.COMPANY
    : DEFAULT_SUNBURST_ROOT;
}

function compareNames(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Carga de Atenção de um conjunto de Ativos.
 *
 * Ativos em Atenção sobre os Ativos **não-neutros**. Fora de Operação fica fora
 * dos dois lados da conta, pelo mesmo motivo da
 * docs/adr/0008-estado-macro-em-tres-niveis.md: ativo desativado ou em
 * manutenção não é problema, é ausência. No denominador, uma Instalação inteira
 * em manutenção pareceria saudável.
 *
 * @param {Object[]} assets Ativos do nó.
 * @returns {Number|null} Fração entre 0 e 1, ou null se não há Ativo não-neutro.
 */
export function getAttentionLoad(assets) {
  const considered = (assets || []).filter(
    (asset) => asset.state !== assetStates.OUT_OF_SERVICE,
  );

  if (!considered.length) {
    return null;
  }

  const troubled = considered.filter(
    (asset) => asset.state === assetStates.ATTENTION,
  ).length;

  return troubled / considered.length;
}

/**
 * Nó de Sensor — o IED. É a unidade de peso do gráfico: vale 1, e todo setor
 * acima dele é a soma dos seus Sensores.
 */
function buildSensorNode(sensor, asset, index) {
  return {
    id: sensor.assetModuleId || `${asset.key}::${index}`,
    key: sensor.assetModuleId || `${asset.key}::${index}`,
    level: sunburstLevels.SENSOR,
    name: sensor.name,
    status: sensor.status,
    company: asset.company,
    installation: asset.installation,
    asset: asset.name,
    value: 1,
    assetCount: 0,
    sensorCount: 1,
    // O Sensor herda o realce do seu Ativo: o Filtro do Painel decide por
    // Ativo inteiro, nunca por Sensor. Ver CONTEXT.md, Filtro de Estado.
    matched: Boolean(asset.matched),
    dimmed: Boolean(asset.dimmed),
    children: [],
  };
}

function buildAssetNode(asset) {
  const sensors = asset.sensors || [];
  const sensorNodes = sensors.map((sensor, index) => buildSensorNode(sensor, asset, index));

  return {
    id: asset.key,
    key: asset.key,
    level: sunburstLevels.ASSET,
    name: asset.name,
    company: asset.company,
    installation: asset.installation,
    state: asset.state,
    // O Ativo ocupa o espaço dos seus Sensores. O piso de 1 protege o caso de um
    // Ativo sem Sensor algum, que sumiria do gráfico com valor zero.
    value: Math.max(sensors.length, 1),
    assetCount: 1,
    sensorCount: sensors.length,
    matched: Boolean(asset.matched),
    dimmed: Boolean(asset.dimmed),
    children: sensorNodes,
  };
}

/**
 * Nó de agrupamento. Não recebe `value`: o ECharts soma os filhos, e um valor
 * próprio maior que a soma abriria uma fatia vazia dentro do anel.
 */
function buildGroupNode(id, level, name, assets, children) {
  return {
    id,
    key: id,
    level,
    name,
    // A Empresa do nó, guardada mesmo quando não vira anel: ordena as
    // Instalações e identifica o setor no tooltip.
    company: assets.length ? assets[0].company : null,
    // Os Ativos que o nó reúne, para o nível de cima consolidar sem reabrir
    // a árvore.
    assets,
    attentionLoad: getAttentionLoad(assets),
    assetCount: assets.length,
    sensorCount: assets.reduce(
      (total, asset) => total + (asset.sensors || []).length,
      0,
    ),
    matched: assets.some((asset) => asset.matched),
    dimmed: assets.length > 0 && assets.every((asset) => asset.dimmed),
    children,
  };
}

function groupBy(items, getKey) {
  const buckets = {};
  const order = [];

  items.forEach((item) => {
    const key = getKey(item);
    if (!buckets[key]) {
      buckets[key] = [];
      order.push(key);
    }
    buckets[key].push(item);
  });

  return order.map((key) => ({ key, items: buckets[key] }));
}

/**
 * Instalações, já com seus Ativos dentro, ordenadas por Empresa e depois por
 * nome. A Empresa participa da ordenação mesmo quando não é desenhada: sem ela,
 * instalações da mesma Empresa ficariam espalhadas pelo círculo.
 */
function compareByCompanyThenName(left, right) {
  return (
    compareNames(left.company, right.company)
    || compareNames(left.name, right.name)
  );
}

function buildInstallationNodes(assets) {
  const nodes = groupBy(assets || [], (asset) => asset.installationId).map(
    ({ key: installationId, items: installationAssets }) => {
      const assetNodes = installationAssets
        .slice()
        .sort((left, right) => compareNames(left.name, right.name))
        .map(buildAssetNode);

      return buildGroupNode(
        `installation::${installationId}`,
        sunburstLevels.INSTALLATION,
        installationAssets[0].installation,
        installationAssets,
        assetNodes,
      );
    },
  );

  return nodes.sort(compareByCompanyThenName);
}

/**
 * Monta a árvore desenhada pelo gráfico.
 *
 * Com raiz em Empresa são três anéis; com raiz em Instalação são dois, e as
 * Instalações de todas as Empresas do Escopo ficam lado a lado.
 *
 * @param {Object[]} assets Ativos de `buildAssets`, já decorados pelo Filtro.
 * @param {String} [rootLevel] `company` ou `installation`.
 * @returns {Object[]} Nós do anel mais interno, ordenados.
 */
export function buildSunburstTree(assets, rootLevel = DEFAULT_SUNBURST_ROOT) {
  const installations = buildInstallationNodes(assets);

  if (rootLevel !== sunburstLevels.COMPANY) {
    return installations;
  }

  return groupBy(installations, (node) => node.company)
    .map(({ key: company, items: companyInstallations }) => {
      const companyAssets = companyInstallations.reduce(
        (all, node) => all.concat(node.assets),
        [],
      );

      return buildGroupNode(
        `company::${company}`,
        sunburstLevels.COMPANY,
        company,
        companyAssets,
        companyInstallations,
      );
    })
    .sort((left, right) => compareNames(left.name, right.name));
}

/**
 * Recorta a árvore no nó apontado pelo caminho de drill-down.
 *
 * O caminho é reaplicado a cada ciclo sobre uma árvore recém-montada. Se o nó
 * apontado desapareceu — mudou o escopo, a instalação saiu do retorno —, o
 * caminho é truncado até o último nó que ainda existe, em vez de devolver vazio:
 * um painel que mostra nada porque perdeu a referência é pior que um que sobe
 * um nível.
 *
 * @param {Object[]} tree Saída de `buildSunburstTree`.
 * @param {String[]} [path] Chaves dos nós atravessados, do topo para baixo.
 * @returns {Object} `{ nodes, trail, path }` — nós a desenhar e a trilha válida.
 */
export function sliceTree(tree, path) {
  const trail = [];
  let nodes = tree || [];

  (path || []).forEach((key) => {
    const found = nodes.filter((node) => node.key === key)[0];
    if (found && found.children.length) {
      trail.push(found);
      nodes = found.children;
    }
  });

  return {
    nodes,
    trail,
    path: trail.map((node) => node.key),
  };
}

/**
 * Ângulo mínimo, em graus, para um anel valer a pena ser desenhado.
 *
 * Abaixo disso o setor fica mais fino que a borda que o separa do vizinho, e o
 * anel deixa de ser leitura para virar textura — foi o que aconteceu com os 524
 * Sensores da frota, a 0,7° cada.
 *
 * Aferido contra a frota real: Instalações 4,4°, Ativos 2,4°, Sensores 0,7°. O
 * limiar fica em 1,5 para cortar os Sensores com folga sem ameaçar o anel de
 * Ativo, que é o coração da tela e passa com pouca margem.
 */
export const MIN_READABLE_ANGLE = 1.5;

const FULL_TURN = 360;

function countByDepth(nodes, depth, counts) {
  if (!nodes.length) {
    return;
  }

  // eslint-disable-next-line no-param-reassign
  counts[depth] = (counts[depth] || 0) + nodes.length;
  nodes.forEach((node) => countByDepth(node.children || [], depth + 1, counts));
}

function trimToDepth(nodes, depth) {
  return nodes.map((node) => ({
    ...node,
    children: depth <= 1 ? [] : trimToDepth(node.children || [], depth - 1),
  }));
}

/**
 * Remove os anéis que não caberiam no círculo.
 *
 * A profundidade do gráfico passa a depender do recorte: a frota inteira para no
 * Ativo, e ao entrar numa Instalação o anel de Sensor aparece, porque lá os
 * mesmos setores passam de 25°.
 *
 * O corte é por anel inteiro, nunca por setor: esconder só os setores estreitos
 * deixaria buracos onde há dado, e quem olhasse concluiria que aqueles Ativos
 * não têm Sensor.
 *
 * @param {Object[]} nodes Nós devolvidos por `sliceTree`.
 * @param {Number} [minAngle] Ângulo mínimo por setor, em graus.
 * @returns {Object[]} Nós com os anéis ilegíveis podados.
 */
export function pruneUnreadableLevels(nodes, minAngle = MIN_READABLE_ANGLE) {
  const counts = [];
  countByDepth(nodes || [], 0, counts);

  const firstUnreadable = counts.findIndex(
    (count) => FULL_TURN / count < minAngle,
  );

  if (firstUnreadable < 0) {
    return nodes || [];
  }

  // O primeiro anel nunca é podado: sem ele não sobra gráfico.
  return trimToDepth(nodes || [], Math.max(firstUnreadable, 1));
}

/**
 * Nós de Ativo alcançáveis a partir de um conjunto de nós, em qualquer
 * profundidade. Usado para saber quem pode pulsar no recorte atual.
 *
 * Para de descer no Ativo: a Pulsação de Alarme marca mudança de Estado do
 * Ativo, e Sensor não tem estado consolidado próprio.
 *
 * @param {Object[]} nodes Nós desenhados.
 * @returns {Object[]} Nós de nível Ativo.
 */
export function collectAssetNodes(nodes) {
  return (nodes || []).reduce((found, node) => {
    if (node.level === sunburstLevels.ASSET) {
      return found.concat([node]);
    }
    return found.concat(collectAssetNodes(node.children));
  }, []);
}
